import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { isMobile } from 'react-device-detect';
import { useBuildingsDispatch } from '../../context/buildings';
import css from '../../styles/modules/buildings/BuildingsCatalogSort.module.scss';

const BuildingsCatalogSort = ({ content }) => {
  /** STATES */
  const setBuildingsState = useBuildingsDispatch();
  const [isVisible, setVisibility] = useState(false);
  const [selected, setSelected] = useState(content.filter((value) => value.selected)[0]);

  /** REFS */
  const parentEl = useRef(null);
  const selectEl = useRef(null);

  /** HANDLERS */
  const handleOutsideClick = (e) => {
    if (e.target.closest(`.${css.sort}`) !== parentEl.current)
      setVisibility(false);
  };

  /** HOOKS */
  useEffect(() => {
    document.body.addEventListener('click', handleOutsideClick);
    return () => document.body.removeEventListener('click', handleOutsideClick);
  }, []);

  /** TEMPLATES */
  if (isMobile)
    return (
      <button
        ref={parentEl}
        type="button"
        className={css.sort}
        onClick={() => selectEl.current.focus()}
      >
        <select
          ref={selectEl}
          onChange={(e) => {
            const selectedOption = [...e.target.options].filter((x) => x.selected)[0];
            const groupedSelected = {
              text: selectedOption.innerText,
              sort: selectedOption.value,
              direction: selectedOption.dataset.direction
            };

            setSelected(groupedSelected);
            setBuildingsState({ type: 'SET_PAGINATION', payload: { page: 1 } });
            setBuildingsState({
              type: 'SET_SORTING',
              payload: {
                sort: groupedSelected.sort,
                sortDirection: groupedSelected.direction
              }
            });
          }}
        >
          {content.map((value, index) => (
            <option
              key={index}
              data-direction={value.direction}
              value={value.sort}
              defaultValue={value.selected ? 'selected' : null}
            >
              {value.text}
            </option>
          ))}
        </select>
        <div className={css.sortShowcase}>
          <div className={css.sortText}>{selected.text}</div>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
          >
            <path d="M8 5V19" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M16 19V5" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            <path
              d="M12 14L8 19L4 14"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M12.0039 10L16.0039 5L20.0039 10"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
      </button>
    );

  return (
    <div ref={parentEl} className={css.sort}>
      <div
        className={css.sortShowcase}
        role="button"
        tabIndex={0}
        onClick={() => setVisibility(!isVisible)}
      >
        <div className={css.sortText}>{selected.text}</div>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
        >
          <path d="M8 5V19" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M16 19V5" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M12 14L8 19L4 14" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          <path
            d="M12.0039 10L16.0039 5L20.0039 10"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </div>
      <AnimatePresence>
        {isVisible && (
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 12, transition: { duration: 0.2 } }}
            className={css.sortPicker}
          >
            {content.map((value, index) => (
              <button
                type="button"
                className={[
                  css.sortElement,
                  value.sort === selected.sort && value.direction === selected.direction
                    ? css.selected
                    : null
                ].join(' ')}
                key={index}
                onClick={(e) => {
                  setVisibility(false);

                  if (!e.target.classList.contains(css.selected)) {
                    setSelected(value);
                    setBuildingsState({ type: 'SET_PAGINATION', payload: { page: 1 } });
                    setBuildingsState({
                      type: 'SET_SORTING',
                      payload: { sort: value.sort, sortDirection: value.direction }
                    });
                  }
                }}
              >
                {value.text}
              </button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default BuildingsCatalogSort;
